import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { newsApi, type NewsArticle } from '@/services/api';

const PAGE_SIZE = 6;

export default function NewsPage() {
  const [activeCategory, setActiveCategory] = useState('全部');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [categories, setCategories] = useState<string[]>(['全部']);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load categories once
  useEffect(() => {
    newsApi
      .categories()
      .then((cats) => setCategories(['全部', ...cats]))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    newsApi
      .list({
        category: activeCategory !== '全部' ? activeCategory : undefined,
        search: searchQuery || undefined,
        page: currentPage,
        pageSize: PAGE_SIZE,
      })
      .then((result) => {
        setArticles(result.list);
        setTotal(result.total);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : '加载失败');
        setLoading(false);
      });
  }, [activeCategory, searchQuery, currentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Navbar />
      <div className="w-full bg-background-50 min-h-screen">
        {/* Hero Banner */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900">
              行业资讯
            </h1>
            <p className="text-foreground-500 text-base mt-4 max-w-xl mx-auto leading-relaxed">
              绿证政策解读、双碳动态追踪、节能改造案例与能耗管理干货，助力企业绿色转型
            </p>

            {/* Search */}
            <div className="mt-8 max-w-lg mx-auto">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="搜索文章标题、关键词..."
                  className="w-full bg-background-50 border border-background-300 rounded-full pl-10 pr-5 py-3 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="w-full border-b border-background-200/70 bg-background-50 sticky top-[57px] z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1.5 overflow-x-auto py-3 flex-nowrap">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    setActiveCategory(cat);
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeCategory === cat
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Article Cards */}
        <section className="w-full py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {loading && (
              <div className="text-center py-20">
                <div className="w-12 h-12 mx-auto rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
                <p className="text-foreground-500 text-sm mt-4">正在加载文章...</p>
              </div>
            )}

            {error && !loading && (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-base mt-4">{error}</p>
                <button
                  onClick={fetchData}
                  className="mt-3 text-primary-600 text-sm hover:underline cursor-pointer"
                >
                  重新加载
                </button>
              </div>
            )}

            {!loading && !error && articles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-file-search-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-base mt-4">未找到相关文章</p>
                <button
                  onClick={() => {
                    setActiveCategory('全部');
                    setSearchQuery('');
                    setCurrentPage(1);
                  }}
                  className="mt-3 text-primary-600 text-sm hover:underline cursor-pointer"
                >
                  清除筛选条件
                </button>
              </div>
            )}

            {!loading && !error && articles.length > 0 && (
              <>
                <div className="text-sm text-foreground-400 mb-6">
                  共 <strong className="text-foreground-700">{total}</strong> 篇文章
                  {activeCategory !== '全部' && (
                    <span className="ml-2">
                      · 分类：<span className="text-primary-600">{activeCategory}</span>
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {articles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/news/${article.id}`}
                      className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer"
                    >
                      <div className="h-48 overflow-hidden">
                        <img
                          src="/images/news-default.jpg"
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-2.5">
                          <span className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded">
                            {article.category}
                          </span>
                          <span className="text-foreground-400 text-xs">{article.publishDate}</span>
                        </div>
                        <h3 className="font-heading text-base font-bold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                          {article.title}
                        </h3>
                        <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2">
                          {article.summary}
                        </p>
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-background-200/50">
                          <span className="text-foreground-400 text-xs">{article.author}</span>
                          <span className="flex items-center gap-1 text-foreground-400 text-xs">
                            <i className="ri-eye-line text-xs"></i>
                            {article.readCount.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-1.5 mt-12">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="w-9 h-9 rounded-md flex items-center justify-center border border-background-300 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-9 h-9 rounded-md text-sm font-medium transition-all duration-200 cursor-pointer ${
                          currentPage === page
                            ? 'bg-primary-500 text-background-50'
                            : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 rounded-md flex items-center justify-center border border-background-300 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Energy System CTA Banner */}
        <section className="w-full py-14 md:py-20 bg-foreground-900">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="flex-1 text-center lg:text-left">
                <span className="text-primary-400 text-xs font-medium tracking-widest uppercase">
                  能耗管理系统
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mt-3 leading-tight">
                  数据驱动能源优化，
                  <br />
                  让每一度电都创造价值
                </h2>
                <p className="text-foreground-400 text-base mt-4 max-w-lg leading-relaxed">
                  覆盖电、水、气、热全品类，500+企业验证，
                  平均节能15-30%。预约演示，开启智能能源管理之旅。
                </p>
                <div className="flex flex-wrap gap-3 mt-6 justify-center lg:justify-start">
                  <a
                    href="https://ems.firmai.cn/web"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 bg-background-50 text-foreground-900 text-sm font-semibold rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    了解更多
                  </a>
                  <a
                    href="https://ems.firmai.cn/web"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 border border-background-50/30 text-background-50 text-sm font-semibold rounded-md hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    预约演示
                  </a>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 w-full lg:w-auto">
                {[
                  { value: '15-30%', label: '平均节能率' },
                  { value: '500+', label: '服务企业数' },
                  { value: '98.6%', label: '数据准确率' },
                  { value: '12月', label: '投资回收期' },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-background-50/5 border border-background-50/10 rounded-lg px-6 py-4 text-center min-w-[120px]"
                  >
                    <p className="text-2xl font-heading font-bold text-background-50">
                      {stat.value}
                    </p>
                    <p className="text-foreground-400 text-xs mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}